/**
 * Validated Policy Form
 * 
 * Production-ready form for creating and managing governance policies with rule engine integration
 */

import {
    Alert,
    Badge,
    Button,
    Card,
    Checkbox,
    Code,
    Group,
    JsonInput,
    MultiSelect,
    NumberInput,
    Paper,
    Select,
    Stack,
    Text,
    Textarea,
    TextInput,
    Title,
    Tooltip,
    ActionIcon,
    Tabs,
    Grid,
    Timeline,
    Progress,
    Modal
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
    IconCheck,
    IconAlertTriangle,
    IconInfoCircle,
    IconX,
    IconTestPipe,
    IconShield,
    IconGavel,
    IconBulb,
    IconPlayerPlay
} from '@tabler/icons-react';
import React, { useState } from 'react';
import { useValidatedForm, useValidatedFormSubmission } from '../hooks/useValidatedForm';
import { usePolicyMutations } from '../data/hooks/usePolicy';

interface PolicyRule {
    id: string;
    condition: string;
    action: 'allow' | 'deny' | 'require_approval' | 'log_warning' | 'escalate';
    parameters?: Record<string, any>;
    priority: number;
    enabled: boolean;
}

interface PolicyData {
    name: string;
    description?: string;
    category: 'security' | 'compliance' | 'business' | 'technical' | 'operational';
    priority: 'low' | 'medium' | 'high' | 'critical';
    scope: string[];
    rules: PolicyRule[];
    enforced: boolean;
    auditRequired: boolean;
    notifications?: {
        onViolation: boolean;
        onCompliance: boolean;
        recipients: string[];
    };
    metadata?: Record<string, any>;
    validFrom?: string;
    validTo?: string;
}

interface ValidatedPolicyFormProps {
    initialData?: Partial<PolicyData>;
    mode: 'create' | 'edit';
    onSuccess?: (data: PolicyData) => void;
    onCancel?: () => void;
}

export const ValidatedPolicyForm: React.FC<ValidatedPolicyFormProps> = ({
    initialData,
    mode,
    onSuccess,
    onCancel
}) => {
    const { createPolicy, updatePolicy, testPolicy } = usePolicyMutations();
    const [activeTab, setActiveTab] = useState<string | null>('basic');
    const [testModalOpened, { open: openTestModal, close: closeTestModal }] = useDisclosure(false);
    const [testData, setTestData] = useState('{}');
    const [testResult, setTestResult] = useState<any>(null);
    const [testLoading, setTestLoading] = useState(false);

    // Initialize validated form
    const form = useValidatedForm<Partial<PolicyData>>({
        schemaName: 'Policy' as any,
        initialValues: {
            name: initialData?.name || '',
            description: initialData?.description || '',
            category: initialData?.category || 'business',
            priority: initialData?.priority || 'medium',
            scope: initialData?.scope || [],
            rules: initialData?.rules || [],
            enforced: initialData?.enforced || false,
            auditRequired: initialData?.auditRequired || false,
            notifications: initialData?.notifications || {
                onViolation: true,
                onCompliance: false,
                recipients: []
            },
            metadata: initialData?.metadata || {},
            validFrom: initialData?.validFrom || undefined,
            validTo: initialData?.validTo || undefined
        },
        validateOnChange: true,
        validateOnBlur: true,
        showNotifications: false
    });

    // Form submission with GraphQL
    const { submitForm, loading: submitting, submissionResult } = useValidatedFormSubmission(
        'Policy' as any,
        () => ({
            mutate: async (data: Partial<PolicyData>) => {
                if (mode === 'create') {
                    return await createPolicy(data as any);
                } else {
                    return await updatePolicy(initialData?.name || '', data as any);
                }
            },
            loading: false,
            error: null
        })
    );

    // Policy category options
    const categoryOptions = [
        { value: 'security', label: 'Security - Access control and data protection policies' },
        { value: 'compliance', label: 'Compliance - Regulatory and legal compliance rules' },
        { value: 'business', label: 'Business - Business logic and workflow rules' },
        { value: 'technical', label: 'Technical - System and infrastructure policies' },
        { value: 'operational', label: 'Operational - Process and operational guidelines' }
    ];

    // Priority options
    const priorityOptions = [
        { value: 'low', label: 'Low Priority' },
        { value: 'medium', label: 'Medium Priority' },
        { value: 'high', label: 'High Priority' },
        { value: 'critical', label: 'Critical Priority' }
    ];

    // Scope options (example domains)
    const scopeOptions = [
        { value: 'user_management', label: 'User Management' },
        { value: 'data_access', label: 'Data Access' },
        { value: 'workflow_execution', label: 'Workflow Execution' },
        { value: 'api_access', label: 'API Access' },
        { value: 'file_operations', label: 'File Operations' },
        { value: 'system_administration', label: 'System Administration' },
        { value: 'reporting', label: 'Reporting' },
        { value: 'integration', label: 'External Integrations' }
    ];

    // Rule action options
    const actionOptions = [
        { value: 'allow', label: 'Allow - Permit the action' },
        { value: 'deny', label: 'Deny - Block the action' },
        { value: 'require_approval', label: 'Require Approval - Request approval before proceeding' },
        { value: 'log_warning', label: 'Log Warning - Allow but log a warning' },
        { value: 'escalate', label: 'Escalate - Forward to higher authority' }
    ];

    // Add new rule
    const addRule = () => {
        const newRule: PolicyRule = {
            id: `rule_${Date.now()}`,
            condition: '',
            action: 'allow',
            priority: form.values.rules?.length || 0,
            enabled: true,
            parameters: {}
        };
        
        form.setFieldValue('rules', [...(form.values.rules || []), newRule]);
    };

    // Update rule
    const updateRule = (index: number, field: keyof PolicyRule, value: any) => {
        const updatedRules = [...(form.values.rules || [])];
        updatedRules[index] = { ...updatedRules[index], [field]: value };
        form.setFieldValue('rules', updatedRules);
    };

    // Remove rule
    const removeRule = (index: number) => {
        const updatedRules = form.values.rules?.filter((_: any, i: number) => i !== index) || [];
        form.setFieldValue('rules', updatedRules);
    };

    // Test policy
    const handleTestPolicy = async () => {
        setTestLoading(true);
        try {
            let parsedTestData;
            try {
                parsedTestData = JSON.parse(testData);
            } catch (error) {
                notifications.show({
                    title: 'Invalid Test Data',
                    message: 'Please provide valid JSON test data',
                    color: 'red'
                });
                setTestLoading(false);
                return;
            }

            const result = await testPolicy(form.values as any, parsedTestData);
            setTestResult(result);
            
            notifications.show({
                title: 'Policy Test Complete',
                message: `Test result: ${result.decision}`,
                color: result.decision === 'allow' ? 'green' : 'orange'
            });
        } catch (error) {
            notifications.show({
                title: 'Test Failed',
                message: (error as Error).message,
                color: 'red'
            });
        } finally {
            setTestLoading(false);
        }
    };

    // Handle form submission
    const handleSubmit = async (values: Partial<PolicyData>) => {
        const result = await submitForm(values);
        if (result.success && onSuccess) {
            onSuccess(result.data);
        }
    };

    const getCategoryColor = (category: string) => {
        switch (category) {
            case 'security': return 'red';
            case 'compliance': return 'blue';
            case 'business': return 'green';
            case 'technical': return 'purple';
            case 'operational': return 'orange';
            default: return 'gray';
        }
    };

    const getPriorityColor = (priority: string) => {
        switch (priority) {
            case 'critical': return 'red';
            case 'high': return 'orange';
            case 'medium': return 'yellow';
            case 'low': return 'blue';
            default: return 'gray';
        }
    };

    return (
        <form onSubmit={form.onSubmit((values) => handleSubmit(values as Partial<PolicyData>))}>
            <Stack gap="lg">
                {/* Header */}
                <Paper p="md" withBorder>
                    <Group justify="space-between">
                        <div>
                            <Group>
                                <Title order={3}>
                                    {mode === 'create' ? 'Create' : 'Edit'} Policy
                                </Title>
                                <IconShield size={24} color="blue" />
                            </Group>
                            <Text size="sm" c="dimmed">
                                Policy data is validated using JSON Schema with rule engine integration
                            </Text>
                        </div>
                        <Group>
                            <Badge 
                                color={form.validationResult.valid ? 'green' : 'red'}
                                leftSection={form.validationResult.valid ? <IconCheck size={14} /> : <IconX size={14} />}
                            >
                                {form.validationResult.valid ? 'Valid' : `${form.validationResult.errors.length} Error${form.validationResult.errors.length > 1 ? 's' : ''}`}
                            </Badge>
                            <Button
                                variant="light"
                                leftSection={<IconTestPipe size={16} />}
                                onClick={openTestModal}
                                disabled={!form.validationResult.valid}
                            >
                                Test Policy
                            </Button>
                        </Group>
                    </Group>
                </Paper>

                {/* Validation Summary */}
                {form.validationResult.errors.length > 0 && (
                    <Alert color="red" title="Validation Errors" icon={<IconAlertTriangle />}>
                        <Stack gap="xs">
                            {form.validationResult.errors.map((error, index) => (
                                <Text key={index} size="sm">
                                    <strong>{error.field}:</strong> {error.message}
                                </Text>
                            ))}
                        </Stack>
                    </Alert>
                )}

                {form.validationResult.warnings && form.validationResult.warnings.length > 0 && (
                    <Alert color="yellow" title="Validation Warnings" icon={<IconInfoCircle />}>
                        <Stack gap="xs">
                            {form.validationResult.warnings.map((warning, index) => (
                                <Text key={index} size="sm">
                                    <strong>{warning.field}:</strong> {warning.message}
                                    {warning.suggestion && <Text c="dimmed"> • {warning.suggestion}</Text>}
                                </Text>
                            ))}
                        </Stack>
                    </Alert>
                )}

                {/* Policy Form Tabs */}
                <Tabs value={activeTab} onChange={(v) => setActiveTab(v ?? '')}>
                    <Tabs.List>
                        <Tabs.Tab value="basic" leftSection={<IconInfoCircle size={16} />}>
                            Basic Info
                        </Tabs.Tab>
                        <Tabs.Tab value="rules" leftSection={<IconGavel size={16} />}>
                            Rules ({form.values.rules?.length || 0})
                        </Tabs.Tab>
                        <Tabs.Tab value="settings" leftSection={<IconShield size={16} />}>
                            Settings
                        </Tabs.Tab>
                    </Tabs.List>

                    {/* Basic Information Tab */}
                    <Tabs.Panel value="basic">
                        <Card withBorder mt="md">
                            <Stack gap="md">
                                <TextInput
                                    label="Policy Name"
                                    placeholder="User Access Control Policy"
                                    required
                                    {...form.getInputProps('name')}
                                    error={form.getFieldError('name')}
                                    rightSection={
                                        form.isFieldValid('name') && form.values.name ? 
                                        <IconCheck size={16} color="green" /> : undefined
                                    }
                                />

                                <Textarea
                                    label="Description"
                                    placeholder="Detailed description of the policy and its purpose..."
                                    minRows={3}
                                    {...form.getInputProps('description')}
                                    error={form.getFieldError('description')}
                                />

                                <Group grow>
                                    <Select
                                        label="Category"
                                        data={categoryOptions}
                                        required
                                        {...form.getInputProps('category')}
                                        error={form.getFieldError('category')}
                                        rightSection={
                                            <Badge size="xs" color={getCategoryColor(form.values.category || '')}>
                                                {form.values.category}
                                            </Badge>
                                        }
                                    />

                                    <Select
                                        label="Priority"
                                        data={priorityOptions}
                                        {...form.getInputProps('priority')}
                                        error={form.getFieldError('priority')}
                                        rightSection={
                                            <Badge size="xs" color={getPriorityColor(form.values.priority || '')}>
                                                {form.values.priority}
                                            </Badge>
                                        }
                                    />
                                </Group>

                                <MultiSelect
                                    label="Scope"
                                    description="Select the areas where this policy applies"
                                    data={scopeOptions}
                                    searchable
                                    value={form.values.scope}
                                    onChange={(values: string[]) => form.setFieldValue('scope', values)}
                                    error={form.getFieldError('scope')}
                                />

                                <Group grow>
                                    <Checkbox
                                        label="Policy Enforced"
                                        description="Enable active enforcement of this policy"
                                        {...form.getInputProps('enforced', { type: 'checkbox' })}
                                    />

                                    <Checkbox
                                        label="Audit Required"
                                        description="Log all policy evaluations for audit purposes"
                                        {...form.getInputProps('auditRequired', { type: 'checkbox' })}
                                    />
                                </Group>
                            </Stack>
                        </Card>
                    </Tabs.Panel>

                    {/* Rules Tab */}
                    <Tabs.Panel value="rules">
                        <Card withBorder mt="md">
                            <Group justify="space-between" mb="md">
                                <Title order={4}>Policy Rules</Title>
                                <Button
                                    size="sm"
                                    variant="light"
                                    leftSection={<IconBulb size={16} />}
                                    onClick={addRule}
                                >
                                    Add Rule
                                </Button>
                            </Group>

                            <Stack gap="md">
                                {form.values.rules?.length === 0 && (
                                    <Text c="dimmed" ta="center" py="xl">
                                        No rules defined. Add rules to specify policy behavior.
                                    </Text>
                                )}

                                {form.values.rules?.map((rule: any, index: number) => (
                                    <Paper key={rule.id} withBorder p="md">
                                        <Stack gap="sm">
                                            <Group justify="space-between">
                                                <Group>
                                                    <Badge variant="light">Rule #{index + 1}</Badge>
                                                    <Badge 
                                                        color={rule.enabled ? 'green' : 'gray'}
                                                        variant={rule.enabled ? 'filled' : 'outline'}
                                                    >
                                                        {rule.enabled ? 'Enabled' : 'Disabled'}
                                                    </Badge>
                                                </Group>
                                                <Group>
                                                    <ActionIcon
                                                        size="sm"
                                                        variant="subtle"
                                                        color="red"
                                                        onClick={() => removeRule(index)}
                                                    >
                                                        <IconX size={16} />
                                                    </ActionIcon>
                                                </Group>
                                            </Group>

                                            <Grid>
                                                <Grid.Col span={6}>
                                                    <Textarea
                                                        label="Condition"
                                                        placeholder="user.role === 'admin' && resource.type === 'sensitive'"
                                                        value={rule.condition}
                                                        onChange={(e: any) => updateRule(index, 'condition', e.currentTarget.value)}
                                                        minRows={2}
                                                        required
                                                    />
                                                </Grid.Col>

                                                <Grid.Col span={6}>
                                                    <Stack gap="sm">
                                                        <Select
                                                            label="Action"
                                                            data={actionOptions}
                                                            value={rule.action}
                                                            onChange={(value: any) => updateRule(index, 'action', value)}
                                                            required
                                                        />

                                                        <Group grow>
                                                            <NumberInput
                                                                label="Priority"
                                                                min={0}
                                                                value={rule.priority}
                                                                onChange={(value: any) => updateRule(index, 'priority', value)}
                                                            />

                                                            <Checkbox
                                                                label="Enabled"
                                                                checked={rule.enabled}
                                                                onChange={(e: any) => updateRule(index, 'enabled', e.currentTarget.checked)}
                                                            />
                                                        </Group>
                                                    </Stack>
                                                </Grid.Col>
                                            </Grid>

                                            {rule.action === 'require_approval' && (
                                                <JsonInput
                                                    label="Parameters"
                                                    placeholder='{ "approvers": ["admin@company.com"], "timeout": 3600 }'
                                                    value={JSON.stringify(rule.parameters || {}, null, 2)}
                                                    onChange={(value: any) => {
                                                        try {
                                                            const parsed = JSON.parse(value || '{}');
                                                            updateRule(index, 'parameters', parsed);
                                                        } catch {
                                                            // Invalid JSON, ignore
                                                        }
                                                    }}
                                                    minRows={2}
                                                    maxRows={4}
                                                />
                                            )}
                                        </Stack>
                                    </Paper>
                                ))}
                            </Stack>
                        </Card>
                    </Tabs.Panel>

                    {/* Settings Tab */}
                    <Tabs.Panel value="settings">
                        <Card withBorder mt="md">
                            <Stack gap="md">
                                <Title order={4}>Notification Settings</Title>
                                
                                <Group>
                                    <Checkbox
                                        label="Notify on Violation"
                                        description="Send notifications when policy is violated"
                                        checked={form.values.notifications?.onViolation || false}
                                        onChange={(e: any) => form.setFieldValue('notifications.onViolation', e.currentTarget.checked)}
                                    />

                                    <Checkbox
                                        label="Notify on Compliance"
                                        description="Send notifications when policy is followed"
                                        checked={form.values.notifications?.onCompliance || false}
                                        onChange={(e: any) => form.setFieldValue('notifications.onCompliance', e.currentTarget.checked)}
                                    />
                                </Group>

                                <MultiSelect
                                    label="Notification Recipients"
                                    placeholder="Enter email addresses"
                                    data={[]}
                                    searchable
                                    value={form.values.notifications?.recipients || []}
                                    onChange={(values: string[]) => form.setFieldValue('notifications.recipients', values)}
                                />

                                <Title order={4} mt="lg">Validity Period</Title>
                                
                                <Group grow>
                                    <TextInput
                                        label="Valid From"
                                        type="datetime-local"
                                        value={form.values.validFrom ? new Date(form.values.validFrom).toISOString().slice(0, 16) : ''}
                                        onChange={(e: any) => form.setFieldValue('validFrom', e.currentTarget.value ? new Date(e.currentTarget.value).toISOString() : undefined)}
                                    />

                                    <TextInput
                                        label="Valid To"
                                        type="datetime-local"
                                        value={form.values.validTo ? new Date(form.values.validTo).toISOString().slice(0, 16) : ''}
                                        onChange={(e: any) => form.setFieldValue('validTo', e.currentTarget.value ? new Date(e.currentTarget.value).toISOString() : undefined)}
                                    />
                                </Group>

                                <JsonInput
                                    label="Additional Metadata (JSON)"
                                    placeholder='{ "department": "security", "compliance_standard": "SOX" }'
                                    validationError="Invalid JSON"
                                    formatOnBlur
                                    autosize
                                    minRows={4}
                                    maxRows={8}
                                    value={typeof form.values.metadata === 'string' ? form.values.metadata : JSON.stringify(form.values.metadata || {}, null, 2)}
                                    onChange={(value: any) => {
                                        try {
                                            const parsed = JSON.parse(value || '{}');
                                            form.setFieldValue('metadata', parsed);
                                        } catch {
                                            form.setFieldValue('metadata', value);
                                        }
                                    }}
                                    error={form.getFieldError('metadata')}
                                />
                            </Stack>
                        </Card>
                    </Tabs.Panel>
                </Tabs>

                {/* Form Actions */}
                <Paper p="md" withBorder>
                    <Group justify="space-between">
                        <div>
                            <Text size="sm" c="dimmed">
                                Policy will be validated before submission
                            </Text>
                            {form.isValidating && (
                                <Text size="sm" c="blue">
                                    Validating...
                                </Text>
                            )}
                        </div>
                        
                        <Group>
                            <Button variant="light" onClick={onCancel}>
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                loading={submitting || form.isValidating}
                                disabled={!form.validationResult.valid}
                            >
                                {mode === 'create' ? 'Create Policy' : 'Update Policy'}
                            </Button>
                        </Group>
                    </Group>
                </Paper>

                {/* Submission Result */}
                {submissionResult && (
                    <Alert 
                        color={submissionResult.success ? 'green' : 'red'}
                        title={submissionResult.success ? 'Success' : 'Error'}
                    >
                        {submissionResult.success ? 
                            'Policy saved successfully!' : 
                            submissionResult.error
                        }
                    </Alert>
                )}

                {/* Test Policy Modal */}
                <Modal opened={testModalOpened} onClose={closeTestModal} title="Test Policy" size="lg">
                    <Stack gap="md">
                        <Text size="sm" c="dimmed">
                            Provide test data to evaluate how this policy would behave
                        </Text>

                        <JsonInput
                            label="Test Data (JSON)"
                            placeholder='{ "user": { "role": "user", "id": "123" }, "resource": { "type": "document", "owner": "456" } }'
                            value={testData}
                            onChange={setTestData}
                            minRows={6}
                            maxRows={10}
                        />

                        {testResult && (
                            <Alert 
                                color={testResult.decision === 'allow' ? 'green' : 'orange'} 
                                title="Test Result"
                                icon={testResult.decision === 'allow' ? <IconCheck /> : <IconX />}
                            >
                                <Stack gap="sm">
                                    <Text><strong>Decision:</strong> {testResult.decision}</Text>
                                    {testResult.reason && (
                                        <Text><strong>Reason:</strong> {testResult.reason}</Text>
                                    )}
                                    {testResult.matchedRules && testResult.matchedRules.length > 0 && (
                                        <div>
                                            <Text><strong>Matched Rules:</strong></Text>
                                            <Timeline mt="sm">
                                                {testResult.matchedRules.map((rule: any, index: number) => (
                                                    <Timeline.Item key={index}>
                                                        <Text size="sm">Rule #{rule.index + 1}: {rule.action}</Text>
                                                    </Timeline.Item>
                                                ))}
                                            </Timeline>
                                        </div>
                                    )}
                                </Stack>
                            </Alert>
                        )}

                        <Group justify="flex-end">
                            <Button variant="light" onClick={closeTestModal}>
                                Close
                            </Button>
                            <Button
                                leftSection={<IconPlayerPlay size={16} />}
                                onClick={handleTestPolicy}
                                loading={testLoading}
                            >
                                Run Test
                            </Button>
                        </Group>
                    </Stack>
                </Modal>
            </Stack>
        </form>
    );
};

export default ValidatedPolicyForm;